import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  CheckCircle,
  Loader2,
  XCircle,
  LogIn,
  ArrowLeft,
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const SignupSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const { theme, setTheme } = useTheme();
  const previousThemeRef = useRef(theme);
  
  const [status, setStatus] = useState('checking'); // checking, completed, expired, error
  const [signupData, setSignupData] = useState(null);
  const [pollCount, setPollCount] = useState(0);
  const maxPolls = 30; // Max 30 attempts (about 1 minute)

  // Force dark mode on signup success page
  useEffect(() => {
    previousThemeRef.current = theme;
    if (theme !== 'dark') {
      setTheme('dark');
    }
    return () => {
      if (previousThemeRef.current !== 'dark') {
        setTheme(previousThemeRef.current);
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    const checkStatus = async () => {
      try {
        const response = await axios.get(`${API_URL}/billing/signup-status/${sessionId}`);
        const data = response.data;
        
        if (data.status === 'completed') {
          setStatus('completed');
          setSignupData(data);
          toast.success('Account created successfully!');
        } else if (data.status === 'expired') {
          setStatus('expired');
        } else if (data.status === 'pending') {
          // Continue polling
          setPollCount(prev => prev + 1);
        }
      } catch (error) {
        console.error('Error checking signup status:', error);
        if (error.response?.status === 404) {
          setStatus('error');
        }
      }
    };

    // Initial check
    checkStatus();

    // Poll every 2 seconds if still pending
    const interval = setInterval(() => {
      if (status === 'checking' && pollCount < maxPolls) {
        checkStatus();
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [sessionId, pollCount, status]);

  // Stop polling after max attempts
  useEffect(() => {
    if (pollCount >= maxPolls && status === 'checking') {
      setStatus('error');
    }
  }, [pollCount, status]);

  const handleGoToLogin = () => {
    navigate('/login');
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4 bg-cover bg-center relative"
      style={{
        backgroundImage: 'url(https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=1920&q=80)',
      }}
    >
      <div className="absolute inset-0 bg-black/60" />
      
      <Card className="w-full max-w-md relative z-10 border-0 shadow-2xl">
        <CardHeader className="text-center pb-4">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-xl overflow-hidden">
              <img src="/equipr-logo.png" alt="EQUIPR" className="w-full h-full object-cover" />
            </div>
          </div>
          <CardTitle className="font-heading text-2xl">EQUIPR CORE</CardTitle>
        </CardHeader>
        
        <CardContent className="text-center">
          {/* Checking Status */}
          {status === 'checking' && (
            <div className="space-y-4">
              <div className="flex justify-center">
                <Loader2 className="h-12 w-12 animate-spin text-[#ffd108]" />
              </div>
              <CardDescription className="text-base">
                Verifying your payment...
              </CardDescription>
              <p className="text-sm text-muted-foreground">
                Please wait while we confirm your subscription
              </p>
            </div>
          )}

          {/* Success */}
          {status === 'completed' && signupData && (
            <div className="space-y-6">
              <div className="flex justify-center">
                <div className="p-4 bg-green-500/20 rounded-full">
                  <CheckCircle className="h-12 w-12 text-green-500" />
                </div>
              </div>
              
              <div className="space-y-2">
                <h2 className="text-xl font-semibold">Welcome to EQUIPR CORE!</h2>
                <CardDescription className="text-base">
                  Your account has been created successfully
                </CardDescription>
              </div>

              <div className="bg-muted/50 rounded-lg p-4 text-left space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Company:</span>
                  <span className="font-medium">{signupData.company_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Client ID:</span>
                  <span className="font-mono font-medium">{signupData.client_id}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Plan:</span>
                  <span className="font-medium capitalize">{signupData.tier}</span>
                </div>
                {signupData.admin_username && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Username:</span>
                    <span className="font-medium">{signupData.admin_username}</span>
                  </div>
                )}
              </div>

              <p className="text-sm text-muted-foreground">
                Use your Client ID and credentials to log in
              </p>

              <Button 
                className="w-full btn-volt" 
                onClick={handleGoToLogin}
              >
                <LogIn className="h-4 w-4 mr-2" />
                Go to Login
              </Button>
            </div>
          )}

          {/* Expired */}
          {status === 'expired' && (
            <div className="space-y-6">
              <div className="flex justify-center">
                <div className="p-4 bg-yellow-500/20 rounded-full">
                  <XCircle className="h-12 w-12 text-yellow-500" />
                </div>
              </div>
              
              <div className="space-y-2">
                <h2 className="text-xl font-semibold">Payment Session Expired</h2>
                <CardDescription className="text-base">
                  Your payment session has expired. Please try again.
                </CardDescription>
              </div>

              <Button 
                className="w-full" 
                variant="outline"
                onClick={handleGoToLogin}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Login
              </Button>
            </div>
          )}

          {/* Error */}
          {status === 'error' && (
            <div className="space-y-6">
              <div className="flex justify-center">
                <div className="p-4 bg-red-500/20 rounded-full">
                  <XCircle className="h-12 w-12 text-red-500" />
                </div>
              </div>
              
              <div className="space-y-2">
                <h2 className="text-xl font-semibold">Something Went Wrong</h2>
                <CardDescription className="text-base">
                  We couldn't verify your payment. If you completed the payment, 
                  please contact support.
                </CardDescription>
              </div>

              <Button 
                className="w-full" 
                variant="outline"
                onClick={handleGoToLogin}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Login
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SignupSuccess;
